'use client';

import { IntlShape, useIntl } from 'react-intl';

import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useIsGDPRDevice, useIsUKGDPRDevice } from '@/hooks/user';
import genericMessages from '@/messages/messages';

import PrivacyPolicyLink from '../PrivacyPolicyLink';
import TermsLink from '../TermsLink';
import { type MessageNode } from '../utils';
import { TermsText, Wrapper } from './styles';

export type AuthTermsProps = {
	/**
	 * @defaultValue
	 * ```tsx
	 * "sisu-auth-terms"
	 * ```
	 */
	'data-testid'?: string;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	preTerms?: MessageNode;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	postTerms?: MessageNode;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	terms?: MessageNode;
};

type Values = Parameters<IntlShape['formatMessage']>[1];

export const useAuthTermsValues = (): Values => {
	const values: Values = {
		terms: <TermsLink />,
		privacypolicylink: chunks => <PrivacyPolicyLink>{chunks}</PrivacyPolicyLink>,
	};
	return values;
};

export default function AuthTerms({
	'data-testid': testId = 'sisu-auth-terms',
	preTerms,
	postTerms,
	terms,
}: AuthTermsProps) {
	const { authPrompt } = useAuthPrompt();
	const { formatMessage } = useIntl();
	const isUKGDPRDevice = useIsUKGDPRDevice();
	const isGDPRDevice = useIsGDPRDevice();

	const useSignupTermsShort = isGDPRDevice || isUKGDPRDevice;

	const values = useAuthTermsValues();

	if (!terms) {
		const signupTerms = useSignupTermsShort ? genericMessages.signupTermsShort : genericMessages.signupTerms;
		terms = formatMessage(authPrompt === 'login' ? genericMessages.loginTerms2 : signupTerms, values);
	}

	return (
		<Wrapper className="loginTerms" data-testid={testId}>
			{preTerms && <TermsText>{preTerms}</TermsText>}
			<TermsText>{terms}</TermsText>
			{postTerms && <TermsText>{postTerms}</TermsText>}
		</Wrapper>
	);
}
